<template>
  <component
    :is="contentComponent"
    id="container"
    ref="root"
    class="sp-navigation"
    :title="collapsableMenuTitle"
    :class="classModifiers"
    :style="styleVariables"
    :expanded="forceCollapse"
    @update-expanded="handleUpdateExpanded"
  >
    <div class="sp-navigation__container" part="container">
      <slot part="default-slot" class="default-slot" @click="handleCollapse" />

      <div v-if="stuckToTop" part="stuck-to-top" @click="handleCollapse">
        <slot name="stuck-to-top" />
      </div>
    </div>
  </component>
</template>

<script setup>
/**
 * The SpNavigation component is used to display a navigation bar.
 *
 * @displayName Navigation
 * @group Custom Elements
 * @component sp-navigation
 */

import { computed, ref, nextTick } from "vue";
import { useBreakpoints } from "../../composables/breakpoints";
import { toBoolean } from "../../utils/props";

const { isGtSmScreen } = useBreakpoints();

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  /**
   * If true the navigation will enable a slot for when it is stuck to the top of the page.
   * This can be used to display additional content when the navigation is stuck to the top.
   *
   * @type {Boolean}
   * @default false
   */
  stuckToTop: {
    type: [Boolean, String],
    default: false,
  },

  /**
   * If true the navigation will be collapsable on small screens.
   * This can be used to display a menu button that will show the navigation when clicked.
   *
   * @type {Boolean}
   * @default false
   */
  collapsable: {
    type: [Boolean, String],
    default: false,
  },

  /**
   * The title to display on the menu button when the navigation is collapsable.
   *
   * @type {String}
   * @default undefined
   */
  collapsableMenuTitle: {
    type: String,
    default: undefined,
  },

  collapseOnContentClick: {
    type: [Boolean, String],
    default: false,
  },

  alwaysCollapsible: {
    type: [Boolean, String],
    default: false,
  },
});

const root = ref(null);

const classModifiers = computed(() => ({
  "--is-stuck-to-top": toBoolean(props.stuckToTop),
}));

const isAlwaysCollapsibleOrSmall = computed(() => toBoolean(props.alwaysCollapsible) || !isGtSmScreen.value);

const styleVariables = computed(() => ({
  "--direction": isAlwaysCollapsibleOrSmall.value ? "column" : "row",
}));

const contentComponent = computed(() =>
  toBoolean(props.collapsable) && isAlwaysCollapsibleOrSmall.value ? "sp-expandable-area" : "nav",
);

const isExpanded = ref(false);
const forceCollapse = ref(false);

function handleUpdateExpanded(event) {
  isExpanded.value = event.detail[0];
}

function handleCollapse() {
  if (toBoolean(props.collapseOnContentClick) && isAlwaysCollapsibleOrSmall.value) {
    if (isExpanded.value) {
      forceCollapse.value = true;
      nextTick(() => {
        forceCollapse.value = false;
      });
    }
  }
}
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped lang="scss">
.sp-navigation {
  --direction: row;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  flex-direction: var(--direction);
  width: 100%;
}

.default-slot {
  display: var(--sp-comp-navigation-default-slot-display, flex);
  width: var(--sp-comp-navigation-default-slot-width, 100%);
  align-items: var(--sp-comp-navigation-default-slot-align-items, center);
  justify-content: var(--sp-comp-navigation-default-slot-justify-content, flex-start);
  gap: 2rem;
}
.sp-navigation__container {
  width: 100%;
}
</style>
